import { useEffect, useMemo, useState } from 'react';
import type { CatalogCategory, CatalogItem } from '@matix/types';
import { useLang } from '../../lib/language';
import { callCenterApi } from './api';
import { useMoneyFormatter } from './hooks';

/**
 * The menu, for someone who looks at it all day.
 *
 * Three decisions drive the layout:
 *
 *   * IMAGES LEAD. An agent recognises a photo faster than they read a name,
 *     and the tenants using this are restaurants and salons whose catalogues
 *     are already photographed. Where a picture is missing the tile falls back
 *     to a large tinted monogram derived from the name — deterministic, so the
 *     same dish is the same colour every shift and stays recognisable by
 *     position and hue even without art.
 *   * THE TREE IS THE TREE. Categories nest (172 → "Cuts & Styling", 179
 *     "Desserts" → children), and flattening them into one strip of 25 pills —
 *     which is what this screen used to do — makes the agent read every label
 *     to find anything. Top level first, drill in, breadcrumb back.
 *   * SEARCH ALWAYS WINS. Typing anything abandons the tree and searches the
 *     whole branch menu, because a caller says "the mango one", not "desserts,
 *     then pretty desserts".
 */
export function CatalogPanel({
  branchId,
  onPick,
}: {
  branchId: number;
  onPick: (item: CatalogItem) => void;
}): JSX.Element {
  const { t, lang } = useLang();
  const money = useMoneyFormatter();

  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [path, setPath] = useState<CatalogCategory[]>([]);
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const current = path[path.length - 1] ?? null;

  useEffect(() => {
    let alive = true;
    setPath([]);
    setItems([]);
    setSearch('');
    void callCenterApi.agent
      .categories(branchId)
      .then((r) => alive && setCategories(r.categories))
      .catch(() => alive && setCategories([]));
    return () => {
      alive = false;
    };
  }, [branchId]);

  const children = useMemo(
    () => categories.filter((c) => (c.parent_id ?? null) === (current?.id ?? null)),
    [categories, current],
  );

  // A category with no children of its own is a leaf; load its items. A parent
  // shows its children instead, and only drills to items at the bottom.
  useEffect(() => {
    if (search.trim()) return;
    if (!current || children.length > 0) {
      setItems([]);
      return;
    }
    let alive = true;
    setLoading(true);
    void callCenterApi.agent
      .items(branchId, current.id)
      .then((r) => alive && setItems(r.items))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [branchId, current, children.length, search]);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) return;
    let alive = true;
    setLoading(true);
    const id = setTimeout(() => {
      void callCenterApi.agent
        .search(branchId, q, 60)
        .then((r) => alive && setItems(r.results))
        .catch(() => alive && setItems([]))
        .finally(() => alive && setLoading(false));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [branchId, search]);

  const searching = search.trim().length >= 2;
  const showCategories = !searching && children.length > 0;

  return (
    <section className="cc-catalog">
      <div className="cc-catalog-bar">
        <input
          className="cc-search cc-search--big"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t({ en: 'Search the whole menu…', ar: 'ابحث في القائمة كاملة…' })}
        />
        {search && (
          <button type="button" className="cc-link" onClick={() => setSearch('')}>
            {t({ en: 'Clear', ar: 'مسح' })}
          </button>
        )}
      </div>

      {!searching && (
        <nav className="cc-crumbs" aria-label={t({ en: 'Menu path', ar: 'مسار القائمة' })}>
          <button type="button" onClick={() => setPath([])} className={path.length === 0 ? 'cc-crumb--on' : ''}>
            {t({ en: 'All', ar: 'الكل' })}
          </button>
          {path.map((c, i) => (
            <button
              key={c.id}
              type="button"
              className={i === path.length - 1 ? 'cc-crumb--on' : ''}
              onClick={() => setPath(path.slice(0, i + 1))}
            >
              {(lang === 'ar' ? c.name_ar || c.name : c.name) ?? `#${c.id}`}
            </button>
          ))}
        </nav>
      )}

      {loading && <div className="cc-hint">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>}

      {showCategories && (
        <div className="cc-tiles">
          {children.map((c) => (
            <button
              key={c.id}
              type="button"
              className="cc-tile cc-tile--cat"
              onClick={() => setPath([...path, c])}
            >
              <Thumb src={c.image_data ?? null} label={c.name ?? String(c.id)} />
              <span className="cc-tile-name">
                {(lang === 'ar' ? c.name_ar || c.name : c.name) ?? `#${c.id}`}
              </span>
            </button>
          ))}
        </div>
      )}

      {!showCategories && (
        <div className="cc-tiles">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              className="cc-tile cc-tile--item"
              onClick={() => onPick(item)}
            >
              <Thumb src={item.image_data ?? null} label={item.name ?? String(item.id)} />
              <span className="cc-tile-name">
                {(lang === 'ar' ? item.name_ar || item.name : item.name) ?? `#${item.id}`}
              </span>
              <span className="cc-tile-price">{money(item.selling_price ?? 0)}</span>
              {(item.modifier_groups?.length ?? 0) > 0 && (
                <span className="cc-tile-badge">
                  {t({ en: 'Options', ar: 'خيارات' })}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {!loading && !showCategories && items.length === 0 && (
        <div className="cc-hint">
          {searching
            ? t({ en: 'Nothing matches that.', ar: 'لا يوجد ما يطابق ذلك.' })
            : t({ en: 'Pick a category to see its items.', ar: 'اختر فئة لعرض أصنافها.' })}
        </div>
      )}
    </section>
  );
}

/**
 * A photo, or a tinted monogram when there is none.
 *
 * The hue is derived from the name, so an item without art still has a stable
 * colour an agent learns — and two adjacent tiles are very unlikely to look
 * alike. A grey placeholder would make a catalogue of unphotographed items
 * unreadable at a glance, which is the state most tenants are actually in.
 */
function Thumb({ src, label }: { src: string | null; label: string }): JSX.Element {
  if (src) return <img className="cc-tile-img" src={src} alt="" loading="lazy" />;
  let hash = 0;
  for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  const hue = hash % 360;
  const initials = label.trim().slice(0, 2).toUpperCase();
  return (
    <span
      className="cc-tile-img cc-tile-img--fallback"
      style={{
        background: `hsl(${hue} 62% 92%)`,
        color: `hsl(${hue} 55% 32%)`,
      }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}
